/**
 * <parallax-hero> — an endless vertical filmstrip of photographs.
 *
 * The strip travels upward at one constant speed and never stops. There are no
 * slides, no transitions and no pauses; the travel itself is what shows each
 * photograph in full as it passes through the frame.
 *
 * All this script does is measure and duplicate. The motion is a single linear
 * CSS animation, so it runs on the compositor and cannot drift between devices —
 * unlike the scroll-driven version this replaced, which behaved differently on
 * desktop and mobile and was impossible to reason about in a background tab.
 */
if (!customElements.get('parallax-hero')) {
  class ParallaxHero extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.parallax-hero__track');
      this.viewport = this.querySelector('.parallax-hero__viewport');
      if (!this.track || !this.viewport) return;

      // Pixels travelled per second. One number decides the whole feel.
      this.speed = Math.max(1, parseFloat(this.dataset.scrollSpeed) || 40);

      this.liftForeground();

      this.originals = [...this.track.querySelectorAll('.parallax-hero__slide')];
      if (this.originals.length === 0) return;

      this.duplicate();

      this.measure();
      // Photographs arrive after layout, and each one changes the strip's height.
      // Remeasure as they land, and on resize, or the loop drifts out of step
      // with the -50% wrap.
      this.imagesReady();
      this.onResize = () => this.measure();
      window.addEventListener('resize', this.onResize, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      this.io?.disconnect();
    }

    /**
     * Move the persistent foreground out of the moving strip and into the
     * viewport. It is rendered inside the track because Liquid's
     * `content_for 'blocks'` puts every block there; left in place it would ride
     * the strip and scroll away with the photographs.
     */
    liftForeground() {
      for (const fg of this.querySelectorAll('.parallax-hero__foreground')) {
        // Its Liquid wrapper is a plain <div class="shopify-block">; move that so
        // the block's own markup and attributes stay intact.
        const node = fg.parentElement?.classList.contains('shopify-block') ? fg.parentElement : fg;
        this.viewport.appendChild(node);
      }
    }

    /** One copy of the set, so travelling exactly -50% lands on an identical
     *  frame and the wrap is invisible. */
    duplicate() {
      if (this.track.querySelector('[data-clone]')) return;
      const fragment = document.createDocumentFragment();
      for (const slide of this.originals) {
        const clone = slide.cloneNode(true);
        clone.setAttribute('data-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        // A duplicate must not be read out twice or hold focus.
        clone.querySelectorAll('a, button, [tabindex]').forEach((el) => el.setAttribute('tabindex', '-1'));
        fragment.appendChild(clone);
      }
      this.track.appendChild(fragment);
    }

    /** Duration is the height of one set divided by the speed, so the pixels-per-
     *  second stays the same whatever the screen width or however many photos. */
    measure() {
      const setHeight = this.originals.reduce((sum, s) => sum + s.getBoundingClientRect().height, 0);
      if (setHeight <= 0) return;
      const seconds = setHeight / this.speed;
      this.track.style.setProperty('--ph-duration', `${seconds.toFixed(2)}s`);
    }

    imagesReady() {
      const imgs = [...this.track.querySelectorAll('img')];
      let pending = imgs.filter((i) => !i.complete).length;
      if (pending === 0) { this.measure(); return; }
      for (const img of imgs) {
        if (img.complete) continue;
        const done = () => { if (--pending <= 0) this.measure(); else this.measure(); };
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      }
    }
  }

  customElements.define('parallax-hero', ParallaxHero);
}
